import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate, useMainRouteParams } from "@/hooks/pane-routing";
import { usePublicCanvasArtifactSafe } from "@/public-canvas/PublicCanvasArtifactContext";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

export interface OpenFileRecordOptions {
  flowSessionId?: string;
}

/**
 * Opens `FileRecordDetailPane` for a given FileRecord id in the right pane.
 *
 * Blocks never stay there: the pane sends a block record on to its canvas,
 * where the lightbox is its full-size surface (see FileRecordDetailPane).
 *
 * On the public canvas-view SPA, opens the CDN-backed public file pane instead.
 *
 * Two-hop navigation when the caller is *inside* a pane context (e.g. a chat
 * bubble rendered by a `SessionPane` that itself lives in `rightPane`).
 */
export function useOpenFileRecord() {
  const appNavigate = useAppNavigate();
  const navigate = useNavigate();
  const paneContext = usePaneRouterContextSafe();
  const { canvasId } = useMainRouteParams();
  const publicCanvas = usePublicCanvasArtifactSafe();

  return useCallback(
    (
      fileRecordId: string,
      flowSessionIdOrOptions?: string | OpenFileRecordOptions,
    ) => {
      if (!fileRecordId) return;

      const options: OpenFileRecordOptions =
        typeof flowSessionIdOrOptions === "string"
          ? { flowSessionId: flowSessionIdOrOptions }
          : (flowSessionIdOrOptions ?? {});
      const { flowSessionId } = options;

      if (publicCanvas) {
        publicCanvas.openPublicFile(fileRecordId);
        return;
      }

      const detailPath = route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId });

      // Standalone chat (inside a pane, no canvas, has session id):
      // Two-hop so the chat slides into main and the file opens in the right pane.
      if (paneContext && !canvasId && flowSessionId) {
        navigate(route(ROUTES.SESSIONS.SESSION, { sessionId: flowSessionId }), {
          state: {
            rightPane: { pathname: detailPath },
          },
        });
        return;
      }

      appNavigate({
        pane: "rightPane",
        to: detailPath,
      });
    },
    [appNavigate, navigate, paneContext, canvasId, publicCanvas],
  );
}

/**
 * Opens `FileRecordDetailPane` with a pre-resolved `FileRecord` passed via
 * route state — used for legacy `base: "image" | "video"` entities that have
 * been adapted inline and don't have a real file_record id in the database.
 */
export function useOpenLegacyEntityAsFileRecord() {
  const appNavigate = useAppNavigate();
  const navigate = useNavigate();
  const paneContext = usePaneRouterContextSafe();
  const { canvasId } = useMainRouteParams();

  return useCallback(
    (record: FileRecord, flowSessionId?: string) => {
      const paneState = { fileRecord: record };
      const detailPath = route(ROUTES.FILE_RECORDS.DETAIL, {
        fileRecordId: record.id,
      });

      if (paneContext && !canvasId && flowSessionId) {
        navigate(route(ROUTES.SESSIONS.SESSION, { sessionId: flowSessionId }), {
          state: {
            rightPane: { pathname: detailPath, state: paneState },
          },
        });
        return;
      }

      appNavigate({
        pane: "rightPane",
        to: detailPath,
        options: { state: paneState },
      });
    },
    [appNavigate, navigate, paneContext, canvasId],
  );
}
